import React from "react";
import { SERVICES, useReveal, useEditorReveal, useMeta } from "./shared.jsx";
import { Crumbs, FinalCTA, Turnstile } from "./shared-ui.jsx";
import { BookingCalendar } from "./BookingCalendar.jsx";
import { HlHeading } from "./sections/headings.jsx";
import content from "../content/kontakt.json";

// Strona kontaktowa (/kontakt). Treść edytowalna z ../content/kontakt.json (CMS /admin/kontakt).
// KontaktBody = wspólny komponent (publiczna strona + podgląd Puck, 1:1). Formularz, kalendarz
// Cal.com i Turnstile to logika interaktywna — STAŁA (nieedytowalna).

const noop = () => {};

const KontaktBody = ({ content: c = content, navigate = noop, editor = false }) => {
  const ref = React.useRef(null);
  useReveal(ref);
  useEditorReveal(ref, editor);
  const [form, setForm] = React.useState({ imie:"", email:"", firma:"", usluga:"", wiadomosc:"", hp:"" });
  const [errors, setErrors] = React.useState({});
  const [sent, setSent] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [turnstileToken, setTurnstileToken] = React.useState(null);
  const set = (k) => (e) => setForm({...form, [k]:e.target.value});
  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.imie.trim()) err.imie = "Podaj imię.";
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) err.email = "Podaj poprawny email.";
    if (!form.firma.trim()) err.firma = "Nazwa firmy.";
    if (!form.usluga) err.usluga = "Wybierz usługę.";
    if (form.wiadomosc.trim().length < 10) err.wiadomosc = "Wiadomość musi mieć co najmniej 10 znaków.";
    if (form.wiadomosc.trim().length > 4000) err.wiadomosc = "Wiadomość przekracza limit 4 000 znaków.";
    setErrors(err);
    if (Object.keys(err).length > 0) {
      setSubmitError(null);
      return;
    }
    if (!turnstileToken) { setSubmitError("Trwa weryfikacja antybotowa — spróbuj ponownie za chwilę."); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, turnstileToken })
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 429) {
          setSubmitError("Zbyt wiele prób w krótkim czasie. Poczekaj minutę i spróbuj ponownie.");
        } else if (res.status === 403 && /antybotow|turnstile/i.test(j.error || "")) {
          setSubmitError("Weryfikacja antybotowa wygasła. Odśwież stronę i wyślij ponownie.");
          setTurnstileToken(null);
          if (window.turnstile?.reset) try { window.turnstile.reset(); } catch {}
        } else if (res.status === 400 && j.error) {
          const fieldMap = { imie: "imie", email: "email", firma: "firma", usluga: "usluga", wiadomosc: "wiadomosc" };
          const matched = Object.keys(fieldMap).find(k => new RegExp(`Invalid ${k}`, "i").test(j.error));
          if (matched) {
            setErrors({ [matched]: "Pole nie spełnia wymagań — sprawdź zawartość." });
          } else {
            setSubmitError("Niektóre pola są niepoprawne. Sprawdź formularz i spróbuj ponownie.");
          }
        } else {
          setSubmitError("Nie udało się wysłać wiadomości. Spróbuj ponownie lub napisz bezpośrednio na hello@letscommerce.pl.");
        }
        return;
      }
      setSent(true);
    } catch (e2) {
      setSubmitError("Brak połączenia. Sprawdź internet i spróbuj ponownie, lub napisz na hello@letscommerce.pl.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div ref={ref}>
      <section className="page-hero bg-tiles container">
        <Crumbs items={[{label:"Home",href:"/"},{label:"Kontakt"}]} navigate={navigate}/>
        <h1 className="h-display rv"><HlHeading before={c.hero.headingBefore} highlight={c.hero.headingHighlight} after={c.hero.headingAfter} /></h1>
        <p className="lead rv" style={{marginTop:28}}>{c.hero.lead}</p>
      </section>

      <section className="sec container">
        <div className="contact-grid">
          <div>
            <span className="eyebrow">{c.booking.eyebrow}</span>
            <h2 className="h2 rv" style={{marginBottom:24}}><HlHeading before={c.booking.headingBefore} highlight={c.booking.headingHighlight} after={c.booking.headingAfter} /></h2>
            <BookingCalendar/>
          </div>
          <div>
            <span className="eyebrow">{c.formSection.eyebrow}</span>
            <h2 className="h2 rv" style={{marginBottom:24}}><HlHeading before={c.formSection.headingBefore} highlight={c.formSection.headingHighlight} after={c.formSection.headingAfter} /></h2>
            {sent ? (
              <div className="form-success">
                <div className="check">✓</div>
                <h3 style={{fontFamily:"var(--font-display)",fontWeight:500,fontSize:24,color:"#fff",margin:"0 0 10px",letterSpacing:"-0.01em"}}>{c.success.heading}</h3>
                <p style={{color:"var(--fg-muted)",margin:0}}>{c.success.body}</p>
              </div>
            ) : (
              <form className="form" onSubmit={submit} noValidate>
                <div className="form-row">
                  <div className={"field" + (errors.imie ? " has-err" : "")}>
                    <label>Imię</label>
                    <input value={form.imie} onChange={set("imie")} placeholder="Jan"/>
                    <span className="err">{errors.imie}</span>
                  </div>
                  <div className={"field" + (errors.email ? " has-err" : "")}>
                    <label>Email</label>
                    <input type="email" value={form.email} onChange={set("email")} placeholder="jan@firma.pl"/>
                    <span className="err">{errors.email}</span>
                  </div>
                </div>
                <div className={"field" + (errors.firma ? " has-err" : "")}>
                  <label>Firma</label>
                  <input value={form.firma} onChange={set("firma")} placeholder="Twoja firma"/>
                  <span className="err">{errors.firma}</span>
                </div>
                <div className={"field" + (errors.usluga ? " has-err" : "")}>
                  <label>Interesująca Cię usługa</label>
                  <select value={form.usluga} onChange={set("usluga")}>
                    <option value="">— wybierz —</option>
                    {SERVICES.map(s => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                    <option value="nie-wiem">Nie wiem jeszcze</option>
                  </select>
                  <span className="err">{errors.usluga}</span>
                </div>
                <div className={"field" + (errors.wiadomosc ? " has-err" : "")}>
                  <label>Wiadomość</label>
                  <textarea value={form.wiadomosc} onChange={set("wiadomosc")} placeholder="Napisz krótko o firmie, skali i co chcesz osiągnąć."/>
                  <span className="err">{errors.wiadomosc}</span>
                </div>
                <div className="hp-field" aria-hidden="true">
                  <label htmlFor="contact-hp">Strona WWW (zostaw puste)</label>
                  <input id="contact-hp" type="text" tabIndex="-1" autoComplete="off" value={form.hp} onChange={set("hp")} />
                </div>
                <Turnstile onToken={setTurnstileToken} />
                {submitError && <div className="form-error" role="alert">{submitError}</div>}
                <button type="submit" disabled={submitting} className="lc-btn lc-btn-primary lc-btn-lg" style={{alignSelf:"flex-start"}}>
                  {submitting ? "Wysyłam…" : "Wyślij wiadomość"} <span className="lc-btn-arr">↗</span>
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="contact-info-grid">
          {(c.contactCards || []).map((card, i) => (
            <div key={i} className="contact-card">
              <h3>{card.title}</h3>
              {(card.rows || []).map((row, j) => (
                <div key={j} className="contact-row">
                  <span className="k">{row.k}</span>
                  {row.href ? <a className="v" href={row.href}>{row.v}</a> : <span className="v">{row.v}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>
      <FinalCTA navigate={navigate}/>
    </div>
  );
};

const KontaktPage = ({ navigate }) => {
  useMeta({ title: content.meta.title, description: content.meta.description });
  return <KontaktBody navigate={navigate} />;
};

export { KontaktPage, KontaktBody };
